import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { BannerAlertType } from '@thoughtspot/radiant-react/widgets/alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { FormBuilder } from '@thoughtspot/radiant-react/widgets/form-builder';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import { Link } from '@thoughtspot/radiant-react/widgets/link';
import { Modal, } from '@thoughtspot/radiant-react/widgets/modal';
import { getCurrentOrgId, isModernFeatureManagementEnabled, isOrgUiEnabled, } from '@thoughtspot/session-service';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useUpgradeValidationLazyQuery } from "../../../services/data-modeling/data-model-service";
import { Admin__FeatureManagementScope, useSetFeatureManagementValueMutation, } from "../../../services/feature-management/feature-management-service";
import { useUpdateLlmProviderConfigMutation } from "../../../services/llm/llm-service";
import { useGetFeatureLazyQuery, useSetTscliCommand, } from "../../../services/tscli/tscli-service";
import { BasePageContext } from "../../../contexts/basepage-context";
import { useAdminEditedPageLeaveContext } from "../../../contexts/context";
import { hasModernFMPrivilege } from '../../../utils/feature-management-privilege';
import { LoaderIndicator } from '../../loader';
import { OverrideOption, PreserveOverrideModal, } from '../../modern-experience/feature-management/preserve-override-modal/preserve-override-modal';
import { ConfigPrePostFixStrings, DATA_MODELING_CONSTANTS, RESTART_FLAGS, SPOTTER_VERSIONS, TranslationsKeys, } from '../common';
import { LlmForm } from '../llm-form/llm-form.container';
import { emptyFormState } from '../llm-form/llm-form-constants';
import { getIsNewLlmConfig, isLlmFormValid } from '../llm-form/llm-form-utils';
import { getIsLLMGatewayEnabled } from '../llm-gateway/llm-gateway.util';
import { LlmVisibilityTable } from '../llm-gateway/llm-visibility-table/llm-visibility-table.container';
import { SettingHeader } from '../setting';
import styles from './editscreen.module.scss';
import editscreenMetricsUtil from './editscreen-metrics-util';
const SCREEN_BLOCK_TIMEOUT = 90000;
export const LLM_CONFIG_ID = 'orion.aiModelType';
export const EditSettings = ({ settingId, pageTitle, configs, configsMap = new Map(), title, titleKey, learnMore, subTitle, backHandler, resetToDefault, isCustomerLLMAcct, providerType, viewMode, allPageSettings, }) => {
    const { metricsService, sessionService } = BasePageContext();
    const provider = providerType?.length
        ? providerType
        : sessionService?.aiModelType();
    const isCustomerLLMAccount = providerType?.length
        ? isCustomerLLMAcct
        : sessionService?.isCustomerLLMAccount();
    const isNewLlmConfig = getIsNewLlmConfig();
    const { t } = useTranslation();
    const formBuilderRef = useRef(null);
    const isEdited = useRef(false);
    const [isConfirm, setIsConfirm] = useState(false);
    const [saveConfirm, setSaveConfirm] = useState(false);
    const [isCancel, setIsCancel] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [upgradeInProgress, setUpgradeInProgress] = useState(false);
    const [isFormValid, setFormValid] = useState(true);
    const timeOutId = useRef();
    const [configPostData, setConfigPostData] = useState(null);
    const [reloadAfterEdit, setReloadAfterEdit] = useState(false);
    const [resetConfigToDefault, setResetConfigToDefault] = useState(false);
    const [clickOnResetConfig, setClickOnResetConfig] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [llmFormErrors, setLlmFormErrors] = useState(emptyFormState);
    const [llmFormData, setLlmFormData] = useState({
        ...emptyFormState,
        usecustomaccount: false,
    });
    const [isBannerVisible, setIsBannerVisible] = React.useState(false);
    const [isPreserveOverrideModalOpen, setIsPreserveOverrideModalOpen,] = useState(false);
    const [pendingConfigPostData, setPendingConfigPostData,] = useState(null);
    const [preserveOverrideConfigName, setPreserveOverrideConfigName,] = useState('');
    const { setFeatureManagementValue, } = useSetFeatureManagementValueMutation();
    const currentOrgId = getCurrentOrgId();
    const shouldUseOrgFMApi = isModernFeatureManagementEnabled() &&
        isOrgUiEnabled() &&
        viewMode !== 'all' &&
        settingId !== 'onboarding';
    const llmConfigExists = configs?.find(config => config.id === LLM_CONFIG_ID) !== undefined;
    const isLlmConnectionsView = getIsLLMGatewayEnabled() &&
        settingId === 'sage_feature' &&
        llmConfigExists;
    const handleLlmFormChange = (formData) => {
        setLlmFormData(formData);
    };
    const ctx = useAdminEditedPageLeaveContext();
    const onboardingPerOrgFlow = settingId === 'onboarding' &&
        sessionService.isEnableOnboardingConfigPerOrg();
    const requiresServiceRestart = RESTART_FLAGS.includes(settingId);
    useEffect(() => () => {
        clearTimeout(timeOutId.current);
    }, [0]);
    useEffect(() => {
        if (reloadAfterEdit) {
            const onBeforeUnload = (e) => {
                e.preventDefault();
                e.returnValue = '';
            };
            window.addEventListener('beforeunload', onBeforeUnload);
            return () => {
                window.removeEventListener('beforeunload', onBeforeUnload);
            };
        }
    }, [reloadAfterEdit]);
    const getTranslations = useMemo(() => (key) => t(`${key}`), [0]);
    const getFullConfigLabel = (configLabel) => {
        let labelText = '';
        if (typeof configLabel === 'string') {
            labelText = configLabel;
        }
        else if (configLabel && typeof configLabel === 'object') {
            labelText = '';
        }
        const safeConfigLabel = labelText || '';
        if (titleKey && safeConfigLabel) {
            const sectionHeaderEn = t(`${titleKey}.${ConfigPrePostFixStrings.TITLE}`, { lng: 'en-US' });
            return `${sectionHeaderEn} - ${safeConfigLabel}`;
        }
        return safeConfigLabel;
    };
    const setUpgrade = (_data) => {
        backHandler({
            isSuccess: true,
            type: 'UPDATE',
            reload: true,
            data: _data,
        });
    };
    const onError = () => {
        setLoading(false);
        backHandler({ isSuccess: false, type: 'UPDATE', reload: false });
    };
    const onComplete = (response) => {
        try {
            if (response.Data !== null) {
                if (settingId === 'onboarding' &&
                    sessionService.isEnableOnboardingConfigPerOrg()) {
                    setLoading(false);
                    backHandler({
                        isSuccess: true,
                        type: 'UPDATE',
                        reload: true,
                        data: response.Data,
                    });
                }
                else if (!requiresServiceRestart) {
                    if (!clickOnResetConfig) {
                        setUpgradeInProgress(false);
                        setLoading(false);
                        setUpgrade(response.Data);
                    }
                    else {
                        setResetConfigToDefault(true);
                    }
                }
                else {
                    timeOutId.current = window.setTimeout(() => {
                        if (!clickOnResetConfig) {
                            setUpgradeInProgress(false);
                            setLoading(false);
                            setUpgrade(response.Data);
                        }
                        else {
                            setResetConfigToDefault(true);
                        }
                    }, SCREEN_BLOCK_TIMEOUT);
                }
            }
            else if (response.Data === null) {
                onError();
            }
        }
        catch {
            onError();
        }
    };
    const onReset = (response) => {
        try {
            if (response.Data !== null) {
                setUpgradeInProgress(false);
                setLoading(false);
                setUpgrade(response.Data);
            }
            else if (response.Data === null) {
                onError();
            }
        }
        catch {
            onError();
        }
    };
    const [updateSetting] = useSetTscliCommand(onComplete, onError, settingId, configPostData, viewMode);
    const { data, loading, getFeature } = useGetFeatureLazyQuery(settingId, viewMode);
    const { updateLlmProviderConfigMutation, data: updateSuccessData, error: llmConfigUpdateError, loading: isSavingData, } = useUpdateLlmProviderConfigMutation();
    useEffect(() => {
        if (updateSuccessData) {
            setLoading(false);
            backHandler({
                isSuccess: true,
                type: 'BACK',
                reload: true,
                data: {
                    isCustomerLLMAccount: llmFormData?.usecustomaccount,
                    provider: llmFormData?.provider,
                },
            });
        }
    }, [updateSuccessData]);
    useEffect(() => {
        if (llmConfigUpdateError?.message) {
            setIsBannerVisible(true);
        }
    }, [llmConfigUpdateError]);
    const { data: validationData, error: validationError, loading: validationLoading, upgradeValidation, } = useUpgradeValidationLazyQuery(false);
    const collectThoughtSpotExperienceEvents = () => {
        let experienceMetricProperties = {};
        let isExperienceModified;
        configs?.forEach((config) => {
            const experienceMetricProperty = editscreenMetricsUtil.experienceMetricProperties[config.id];
            if (experienceMetricProperty) {
                if (configsMap.has(config.id) &&
                    `${configsMap.get(config.id).value}` !== `${config.value}`) {
                    isExperienceModified = true;
                }
                experienceMetricProperties = {
                    ...experienceMetricProperties,
                    ...experienceMetricProperty(configsMap.get(config.id)?.value, config.value),
                };
            }
        });
        if (isExperienceModified) {
            metricsService.collectEvent(new UserAction(userActionTypeEnum.CLUSTER_EXPERIENCE_SWITCH), experienceMetricProperties);
        }
    };
    const collectVisualizationEvents = () => {
        let visualizationMetricProperty = {};
        let isDisclaimerTextModified;
        configs?.forEach((config) => {
            const disclaimerTextMetricProperty = editscreenMetricsUtil.disclaimerTextMetricProperty[config.id];
            if (disclaimerTextMetricProperty) {
                if (configsMap.has(config.id) &&
                    `${configsMap.get(config.id).value}` !== `${config.value}`) {
                    isDisclaimerTextModified = true;
                }
                visualizationMetricProperty = {
                    ...visualizationMetricProperty,
                    ...disclaimerTextMetricProperty(config.value),
                };
            }
        });
        if (isDisclaimerTextModified) {
            metricsService.collectEvent(new UserAction(userActionTypeEnum.SET_LEGAL_DISCLAIMER_FOOTER_TEXT), visualizationMetricProperty);
        }
    };
    useEffect(() => {
        if (configPostData) {
            setLoading(true);
            if (!onboardingPerOrgFlow) {
                setUpgradeInProgress(true);
            }
            updateSetting();
            collectThoughtSpotExperienceEvents();
            collectVisualizationEvents();
            editscreenMetricsUtil.collectEarlyAccessFeatureEvents(settingId, configs, configsMap, metricsService, clickOnResetConfig);
            editscreenMetricsUtil.collectDMFeatureEvents(settingId, configs, configsMap, metricsService);
        }
    }, [configPostData]);
    useEffect(() => {
        if (resetConfigToDefault) {
            getFeature();
        }
    }, [resetConfigToDefault]);
    useEffect(() => {
        if (resetConfigToDefault && !loading && data) {
            if (data.Data) {
                onReset(data);
            }
            else if (data.Error) {
                onError();
            }
        }
    }, [resetConfigToDefault, loading, data]);
    const refreshAfterFMSave = () => {
        setUpgradeInProgress(false);
        setLoading(false);
        backHandler({
            isSuccess: true,
            type: 'UPDATE',
            reload: true,
            data: null,
        });
    };
    const getBackendValue = (formField) => {
        let backendValue = formField.value;
        if (formField.id ===
            'orion.emailConfig.welcomeEmailConfig.customMessage') {
            backendValue = String(backendValue).replaceAll('"', "'");
        }
        if (formField.id === 'ac_default_bucket_for_date' &&
            (formField.value === 'default' || formField.value === 'Detailed')) {
            backendValue = -1;
        }
        return String(backendValue);
    };
    const onSubmitCB = () => {
        const currentFormData = formBuilderRef.current.onSubmitForm();
        const newConfigPostData = {
            configOperation: 'add',
        };
        const configOptions = currentFormData.map((formField) => {
            const labelToUse = formField.config.labelText !== undefined
                ? formField.config.labelText
                : formField.config.label;
            return {
                optionKey: formField.id,
                optionValue: btoa(getBackendValue(formField)),
                optionLabel: getFullConfigLabel(labelToUse),
            };
        });
        newConfigPostData.configOptions = configOptions;
        setConfigPostData(newConfigPostData);
    };
    useEffect(() => {
        if (validationData) {
            setLoading(false);
            if (validationData.failureCount === 0 &&
                validationData.incompleteCount === 0) {
                setValidationMessage(getTranslations(TranslationsKeys.DATA_MODEL_VALIDATE_SUCCESS_MESSAGE));
            }
            else if (validationData.failureCount > 0 ||
                validationData.incompleteCount > 0) {
                setValidationMessage(getTranslations(TranslationsKeys.DATA_MODEL_VALIDATE_FAILURE_MESSAGE));
            }
        }
    }, [validationData]);
    useEffect(() => {
        if (validationError) {
            setLoading(false);
            setValidationMessage(getTranslations(TranslationsKeys.DATA_MODEL_VALIDATE_FAILURE_MESSAGE));
        }
    }, [validationError]);
    const onValidateButtonClick = () => {
        setValidationMessage('');
        upgradeValidation();
        setLoading(true);
    };
    const saveLLMConfig = () => {
        updateLlmProviderConfigMutation(llmFormData.provider, llmFormData);
        metricsService.collectEvent(new UserAction(userActionTypeEnum.LLM_CONFIGURATION_SAVED), {
            previous: isCustomerLLMAccount
                ? t('admin.settingsContent.customerAccount')
                : t('admin.settingsContent.thoughtspot'),
            new: llmFormData.usecustomaccount
                ? t('admin.settingsContent.customerAccount')
                : t('admin.settingsContent.thoughtspot'),
        });
    };
    const getSpotterConfigValue = () => {
        const currentFormData = formBuilderRef.current?.onSubmitForm() || [];
        const spotterConfig = currentFormData.find(field => field?.id === 'orion.spotterExperience');
        return spotterConfig?.value;
    };
    const shouldSkipSpotterVersionConfirm = () => {
        const spotterValue = getSpotterConfigValue();
        return (spotterValue &&
            [
                SPOTTER_VERSIONS.SPOTTER_CLASSIC,
                SPOTTER_VERSIONS.SPOTTER_AGENTIC,
            ].includes(spotterValue));
    };
    const getSpotterVersionConfirmPromptBody = () => {
        return getTranslations(TranslationsKeys.SPOTTER_SAVE_CONFIRMATION_MODAL_BODY_SPOTTER3);
    };
    const logSpotterVersionChange = () => {
        metricsService.collectEvent(new UserAction(userActionTypeEnum.SPOTTER_AGENT_EXPERIENCE_CHANGE), {
            oldVersion: configsMap.get('orion.spotterExperience')?.value,
            newVersion: configs[0]?.value,
        });
    };
    const buildSplitFMConfigData = () => {
        const currentFormData = formBuilderRef.current?.onSubmitForm() ?? [];
        const orgAwareLookup = new Map();
        configs?.forEach((c) => {
            if (c.id) {
                orgAwareLookup.set(c.id, Boolean(c.orgAware));
            }
        });
        const orgAwareFMOptions = [];
        const nonOrgAwareTscliOptions = [];
        currentFormData.forEach((formField) => {
            const isOrgAwareConfig = orgAwareLookup.get(formField.id) ?? false;
            const labelToUse = formField.config.labelText !== undefined
                ? formField.config.labelText
                : formField.config.label;
            if (isOrgAwareConfig) {
                orgAwareFMOptions.push({
                    feature_identifier: formField.id,
                    feature_value: getBackendValue(formField),
                });
            }
            else {
                nonOrgAwareTscliOptions.push({
                    optionKey: formField.id,
                    optionValue: btoa(getBackendValue(formField)),
                    optionLabel: getFullConfigLabel(labelToUse),
                });
            }
        });
        return {
            fmConfigOptions: orgAwareFMOptions,
            tscliConfigOptions: nonOrgAwareTscliOptions,
        };
    };
    const onPreserveOverrideConfirm = (overrideOption) => {
        setIsPreserveOverrideModalOpen(false);
        const splitData = pendingConfigPostData;
        setPendingConfigPostData(null);
        if (!splitData)
            return;
        const removeOrgOverrides = overrideOption === OverrideOption.IGNORE;
        const fmOptions = splitData.fmConfigOptions;
        const tscliOptions = splitData.tscliConfigOptions;
        const onAllDone = () => {
            collectThoughtSpotExperienceEvents();
            collectVisualizationEvents();
            editscreenMetricsUtil.collectEarlyAccessFeatureEvents(settingId, configs, configsMap, metricsService, clickOnResetConfig);
            editscreenMetricsUtil.collectDMFeatureEvents(settingId, configs, configsMap, metricsService);
            ctx.isNotEdited();
            ctx.onSaveClick();
            refreshAfterFMSave();
        };
        const fireTscliOrDone = () => {
            if (tscliOptions.length === 0) {
                onAllDone();
                return;
            }
            collectThoughtSpotExperienceEvents();
            collectVisualizationEvents();
            editscreenMetricsUtil.collectEarlyAccessFeatureEvents(settingId, configs, configsMap, metricsService, clickOnResetConfig);
            editscreenMetricsUtil.collectDMFeatureEvents(settingId, configs, configsMap, metricsService);
            ctx.isNotEdited();
            ctx.onSaveClick();
            setConfigPostData({
                configOperation: 'add',
                configOptions: tscliOptions,
            });
        };
        setLoading(true);
        const saveFMSequentially = (index) => {
            if (index >= fmOptions.length) {
                fireTscliOrDone();
                return;
            }
            const option = fmOptions[index];
            setFeatureManagementValue({
                scope: Admin__FeatureManagementScope.Cluster,
                feature_identifier: option.feature_identifier,
                feature_value: option.feature_value,
                reset_org_overrides: removeOrgOverrides,
            }, () => saveFMSequentially(index + 1), () => onError());
        };
        saveFMSequentially(0);
    };
    const saveWithOrgFMApi = () => {
        const splitData = buildSplitFMConfigData();
        const { fmConfigOptions, tscliConfigOptions } = splitData;
        if (fmConfigOptions.length === 0) {
            onSubmitCB();
            ctx.isNotEdited();
            ctx.onSaveClick();
            return;
        }
        setLoading(true);
        const onFMDone = () => {
            collectThoughtSpotExperienceEvents();
            collectVisualizationEvents();
            editscreenMetricsUtil.collectEarlyAccessFeatureEvents(settingId, configs, configsMap, metricsService, clickOnResetConfig);
            editscreenMetricsUtil.collectDMFeatureEvents(settingId, configs, configsMap, metricsService);
            ctx.isNotEdited();
            ctx.onSaveClick();
            if (tscliConfigOptions.length > 0) {
                setConfigPostData({
                    configOperation: 'add',
                    configOptions: tscliConfigOptions,
                });
            }
            else {
                refreshAfterFMSave();
            }
        };
        const saveSequentially = (index) => {
            if (index >= fmConfigOptions.length) {
                onFMDone();
                return;
            }
            const option = fmConfigOptions[index];
            setFeatureManagementValue({
                scope: Admin__FeatureManagementScope.Org,
                org_identifier: currentOrgId,
                feature_identifier: option.feature_identifier,
                feature_value: option.feature_value,
            }, () => saveSequentially(index + 1), () => onError());
        };
        saveSequentially(0);
    };
    const saveWithCorrectPath = () => {
        if (isModernFeatureManagementEnabled() && viewMode === 'all' && hasModernFMPrivilege()) {
            const splitData = buildSplitFMConfigData();
            if (splitData.fmConfigOptions.length > 0) {
                setPendingConfigPostData(splitData);
                setPreserveOverrideConfigName(title ?? '');
                setIsPreserveOverrideModalOpen(true);
            }
            else {
                onSubmitCB();
                ctx.isNotEdited();
                ctx.onSaveClick();
            }
        }
        else if (shouldUseOrgFMApi) {
            saveWithOrgFMApi();
        }
        else {
            onSubmitCB();
            ctx.isNotEdited();
            ctx.onSaveClick();
        }
    };
    const onSaveClicked = () => {
        if (onboardingPerOrgFlow) {
            onSubmitCB();
            ctx.isNotEdited();
            ctx.onSaveClick();
        }
        else if (llmConfigExists && isNewLlmConfig) {
            const { isValid, formErrors } = isLlmFormValid(llmFormData);
            setLlmFormErrors({ ...formErrors });
            if (isValid)
                saveLLMConfig();
        }
        else if (configs[0]?.id === 'orion.spotterExperience' &&
            shouldSkipSpotterVersionConfirm()) {
            logSpotterVersionChange();
            saveWithCorrectPath();
        }
        else if (configs[0]?.id === 'orion.spotterExperience' ||
            requiresServiceRestart) {
            setSaveConfirm(true);
        }
        else {
            saveWithCorrectPath();
        }
    };
    const onSubmitRenderer = () => {
        return (React.createElement(Button, { isDisabled: !isFormValid, onClick: onSaveClicked, text: getTranslations(TranslationsKeys.SAVE_BUTTON), isSaving: isSavingData, type: "primary" }));
    };
    const onCancelRenderer = () => (React.createElement(Button, { onClick: () => {
            if (!llmConfigExists || !isNewLlmConfig) {
                backHandler({
                    isSuccess: false,
                    type: 'BACK',
                    reload: false,
                });
                ctx.isNotEdited();
                ctx.onSaveClick();
            }
            if (llmConfigExists && isNewLlmConfig) {
                setIsCancel(true);
                metricsService.collectEvent(new UserAction(userActionTypeEnum.LLM_CONFIGURATION_CANCELED), {
                    previous: isCustomerLLMAccount
                        ? t('admin.settingsContent.customerAccount')
                        : t('admin.settingsContent.thoughtspot'),
                    new: llmFormData.usecustomaccount
                        ? t('admin.settingsContent.customerAccount')
                        : t('admin.settingsContent.thoughtspot'),
                });
            }
        }, text: getTranslations(TranslationsKeys.CANCEL_BUTTON), type: "secondary" }));
    const onResetToDefault = () => (React.createElement(Link, { className: styles.resetUnderlineNone, onClick: () => {
            ctx.isNotEdited();
            ctx.onSaveClick();
            const currentFormData = formBuilderRef.current.onSubmitForm();
            const configData = {
                configOperation: 'reset',
            };
            let configOptions = currentFormData.map((formField) => ({
                optionKey: formField.id,
                optionValue: '',
                optionLabel: getFullConfigLabel(formField.config.labelText !== undefined
                    ? formField.config.labelText
                    : formField.config.label),
            }));
            configOptions = [
                ...configOptions,
                ...(onboardingPerOrgFlow
                    ? [
                        {
                            optionKey: 'isresetting',
                            optionValue: 'true',
                        },
                    ]
                    : []),
            ];
            configData.configOptions = configOptions;
            setConfigPostData(configData);
            setClickOnResetConfig(true);
        } }, getTranslations(TranslationsKeys.RESET_BUTTON)));
    const renderValidateButton = () => (React.createElement("div", { className: styles.editContainer },
        React.createElement("div", { className: styles.buttonRow },
            React.createElement("div", { className: styles.innerContainer }, getTranslations(TranslationsKeys.DATA_MODEL_VALIDATE_BUTTON_TITLE)),
            React.createElement("div", { className: styles.buttonSubmitContainer },
                React.createElement(Button, { text: getTranslations(TranslationsKeys.DATA_MODEL_VALIDATE_BUTTON), type: "secondary", onClick: onValidateButtonClick, "data-testid": "validateBtn" }))),
        React.createElement("div", { className: styles.successMessageblock }, validationMessage)));
    const formActionButtons = () => (React.createElement("div", { className: styles.editContainer },
        React.createElement("div", { className: styles.buttonRow },
            React.createElement("div", { className: styles.innerContainer }, "\u00A0"),
            React.createElement("div", { className: styles.buttonSubmitContainer },
                onSubmitRenderer(),
                React.createElement("div", { className: styles.cancelBtnMargin }, onCancelRenderer()))),
        React.createElement("div", { className: styles.buttonReset },
            React.createElement("div", { className: styles.innerContainer }, "\u00A0"),
            resetToDefault &&
                resetToDefault === 'true' &&
                !shouldUseOrgFMApi ? (React.createElement("div", { className: styles.buttonResetContainer }, onResetToDefault())) : null)));
    const backHandlerCB = (type) => {
        if (isEdited.current) {
            setIsConfirm(true);
        }
        else {
            backHandler({ isSuccess: false, type, reload: false });
        }
    };
    const backModalProp = {
        title: getTranslations(TranslationsKeys.MODAL_TITLE),
        isOpen: isConfirm,
        dismissButton: {
            text: getTranslations(TranslationsKeys.MODAL_CANCEL_BTN),
        },
        onDismiss: () => {
            setIsConfirm(false);
        },
        confirmButton: {
            text: getTranslations(TranslationsKeys.MODAL_CONFIRM_BTN),
            onConfirm: () => {
                backHandler({
                    isSuccess: false,
                    type: 'BACK',
                    reload: false,
                });
            },
        },
        body: getTranslations(TranslationsKeys.MODAL_CONTENT),
    };
    const leaveModalProp = {
        title: getTranslations(TranslationsKeys.MODAL_TITLE),
        isOpen: ctx.sideTabClicked,
        dismissButton: {
            text: getTranslations(TranslationsKeys.MODAL_CANCEL_BTN),
        },
        onDismiss: () => {
            ctx.onSaveClick();
        },
        confirmButton: {
            text: getTranslations(TranslationsKeys.MODAL_CONFIRM_BTN),
            onConfirm: () => {
                ctx.onSaveClick();
                ctx.isNotEdited();
                backHandler({
                    isSuccess: false,
                    type: 'BACK',
                    reload: false,
                });
            },
        },
        body: getTranslations(TranslationsKeys.MODAL_CONTENT),
    };
    const getSpotterVersionConfirmModalTitle = () => {
        return getTranslations(TranslationsKeys.SPOTTER_SAVE_CONFIRMATION_MODAL_TITLE_SPOTTER3);
    };
    const saveSpotterVersionConfirmModalProps = {
        title: getSpotterVersionConfirmModalTitle(),
        isOpen: saveConfirm,
        dismissButton: {
            text: getTranslations(TranslationsKeys.SAVECONFIRMATION_MODAL_CANCEL_BTN),
        },
        onDismiss: () => {
            setSaveConfirm(false);
        },
        confirmButton: {
            text: getTranslations(TranslationsKeys.SAVECONFIRMATION_MODAL_CONTINUE_BTN),
            onConfirm: () => {
                logSpotterVersionChange();
                setSaveConfirm(false);
                saveWithCorrectPath();
            },
        },
        body: getSpotterVersionConfirmPromptBody(),
    };
    const saveConfirmModalProps = {
        title: getTranslations(TranslationsKeys.SAVECONFIRMATION_MODAL_TITLE),
        isOpen: saveConfirm,
        dismissButton: {
            text: getTranslations(TranslationsKeys.SAVECONFIRMATION_MODAL_CANCEL_BTN),
        },
        onDismiss: () => {
            setSaveConfirm(false);
        },
        confirmButton: {
            text: getTranslations(TranslationsKeys.SAVECONFIRMATION_MODAL_CONFIRM_BTN),
            onConfirm: () => {
                setSaveConfirm(false);
                onSubmitCB();
                ctx.isNotEdited();
                ctx.onSaveClick();
            },
        },
        body: getTranslations(TranslationsKeys.SAVECONFIRMATION_MODAL_CONTENT),
    };
    const cancelModalProps = {
        title: getTranslations(TranslationsKeys.CANCEL_MODAL_TITLE),
        isOpen: isCancel,
        dismissButton: {
            text: getTranslations(TranslationsKeys.CANCEL_MODAL_CANCEL_BTN),
        },
        onDismiss: () => {
            setIsCancel(false);
        },
        confirmButton: {
            text: getTranslations(TranslationsKeys.CANCEL_MODAL_CONFIRM_BTN),
            onConfirm: () => {
                setLlmFormErrors({ ...emptyFormState });
                backHandler({
                    isSuccess: false,
                    type: 'BACK',
                    reload: false,
                });
            },
        },
        body: getTranslations(TranslationsKeys.CANCEL_MODAL_CONTENT),
    };
    const createModal = (body, { title: ModalTitle, isOpen, dismissButton, onDismiss, confirmButton, }) => (React.createElement(Modal, { title: ModalTitle, isOpen: isOpen, dismissButton: dismissButton, onDismiss: onDismiss, size: 'small', confirmButton: confirmButton },
        React.createElement("div", null, body)));
    const blockUserUI = () => upgradeInProgress && !onboardingPerOrgFlow && requiresServiceRestart
        ? ReactDOM.createPortal(React.createElement("div", { className: styles.editScreenBlockUsage }), document.body)
        : null;
    const configsToShow = isNewLlmConfig
        ? configs.filter(config => config.id !== LLM_CONFIG_ID)
        : configs;
    return (React.createElement(React.Fragment, null,
        React.createElement(GlobalAlert.Banner, { isVisible: isBannerVisible, message: llmConfigUpdateError?.message, type: BannerAlertType.ERROR, onClose: () => setIsBannerVisible(false) }),
        blockUserUI(),
        ctx.sideTabClicked &&
            ctx.edit &&
            createModal(leaveModalProp.body, leaveModalProp),
        isConfirm && createModal(backModalProp.body, backModalProp),
        saveConfirm &&
            createModal(configs[0]?.id === 'orion.spotterExperience'
                ? saveSpotterVersionConfirmModalProps.body
                : saveConfirmModalProps.body, configs[0]?.id === 'orion.spotterExperience'
                ? saveSpotterVersionConfirmModalProps
                : saveConfirmModalProps),
        isCancel && createModal(cancelModalProps.body, cancelModalProps),
        React.createElement(PreserveOverrideModal, { isOpen: isPreserveOverrideModalOpen, configName: preserveOverrideConfigName, onDismiss: () => {
                setIsPreserveOverrideModalOpen(false);
                setPendingConfigPostData(null);
            }, onUpdate: onPreserveOverrideConfirm }),
        isLoading && (React.createElement(LoaderIndicator, { loadingText: (settingId === 'onboarding' &&
                sessionService.isEnableOnboardingConfigPerOrg()) ||
                validationLoading
                ? undefined
                : getTranslations(TranslationsKeys.SAVECONFIRMATION_LOADING_MESSAGE), hideLoader: !isLoading, className: styles.fullScreenLoader })),
        React.createElement("div", null,
            React.createElement("div", { className: styles.editContainerRightContent }, isLlmConnectionsView ? (React.createElement(Vertical, { className: styles.formContainer },
                React.createElement(LlmVisibilityTable, { onBack: () => backHandlerCB('BACK'), sageFeatureConfigs: allPageSettings
                        ?.flatMap((setting) => setting.configs ?? [])
                        .map((c) => ({
                        featureName: c.configName,
                        featureValue: String(c.value ?? ''),
                    })) }))) : (React.createElement(React.Fragment, null,
                React.createElement(SettingHeader, { titleSize: "large", settingName: title, settingSubTitle: subTitle, learnMore: learnMore, actionHandler: (type) => {
                        backHandlerCB(type);
                    } }),
                settingId ===
                    DATA_MODELING_CONSTANTS.DATA_MODEL_TAB &&
                    configs?.[0]?.id ===
                        DATA_MODELING_CONSTANTS.UPGRADING_DATA_MODEL_FLAG &&
                    renderValidateButton(),
                configsToShow && (React.createElement("div", { className: styles.formContainer },
                    React.createElement(FormBuilder, { ref: formBuilderRef, itemLayout: "horizontal", formFields: configsToShow, viewMode: "edit", isEdited: (edited) => {
                            setReloadAfterEdit(edited);
                            ctx.isEdited(edited);
                            isEdited.current = edited;
                            const currentFormData = formBuilderRef.current.onSubmitForm();
                            for (let i = 0; i < currentFormData.length; i++) {
                                const formField = currentFormData[i];
                                if (formField &&
                                    formField.valid === false) {
                                    setFormValid(false);
                                    return;
                                }
                            }
                            setFormValid(true);
                        } }))),
                llmConfigExists &&
                    isNewLlmConfig &&
                    !getIsLLMGatewayEnabled() && (React.createElement(Vertical, { className: styles.formContainer },
                    React.createElement(LlmForm, { onFormChange: handleLlmFormChange, providerSelected: provider, isCustomerLLMAccount: isCustomerLLMAccount, formErrors: llmFormErrors, setLlmFormErrors: setLlmFormErrors }))),
                formActionButtons()))))));
};
//# sourceMappingURL=editscreen.container.js.map